export const useDrawer = () => {
  // Drawer state
  const isDrawerOpen = useState('drawer-open', () => false)
  const drawerTitle = useState('drawer-title', () => '')
  const drawerContent = useState('drawer-content', () => null)
  const drawerHistory = useState('drawer-history', () => [])

  // Track whether we're navigating from within the drawer
  const isInternalNavigation = useState('drawer-internal-navigation', () => false)

  // Functions
  const openDrawer = (options) => {
    const { component, data, title } = options
    const newContent = { component, data, title }

    // If drawer is already open and we're opening a different member/entity
    if (isDrawerOpen.value && !isInternalNavigation.value) {
      const currentData = drawerContent.value?.data
      const isDifferentEntity = currentData?.id !== data?.id

      if (isDifferentEntity) {
        // Opening a different entity from outside - reset history completely
        drawerContent.value = newContent
        drawerTitle.value = title || ''
        drawerHistory.value = [newContent]
      }
      else {
        // Same entity - just update the content
        drawerContent.value = newContent
        drawerTitle.value = title || ''
      }
    }
    else {
      // Either first open or internal navigation - keep history
      drawerContent.value = newContent
      drawerTitle.value = title || ''

      // Add to history if we're navigating internally or first opening
      if (isInternalNavigation.value || drawerHistory.value.length === 0) {
        // Prevent duplicate history entries
        if (drawerHistory.value.length === 0
          || drawerHistory.value[drawerHistory.value.length - 1].data?.id !== data?.id) {
          drawerHistory.value.push(newContent)
        }
      }
    }

    // Reset internal navigation flag after use
    isInternalNavigation.value = false
    isDrawerOpen.value = true
  }

  const closeDrawer = () => {
    isDrawerOpen.value = false
    // Clear history when closing
    drawerHistory.value = []
    isInternalNavigation.value = false
  }

  const goBackInDrawer = () => {
    // Remove current item
    drawerHistory.value.pop()

    // Get the previous item
    const previousContent = drawerHistory.value[drawerHistory.value.length - 1]

    if (previousContent) {
      isInternalNavigation.value = true
      drawerContent.value = previousContent
      drawerTitle.value = previousContent.title || ''
    }
    else {
      closeDrawer()
    }
  }

  // Modify resetDrawerHistory to clear the flag too
  const resetDrawerHistory = () => {
    // Clear history except for the current item
    const current = drawerContent.value
    drawerHistory.value = current ? [current] : []
    isInternalNavigation.value = false
  }

  // Reset function for testing and initialization
  const resetDrawerState = () => {
    isDrawerOpen.value = false
    drawerTitle.value = ''
    drawerContent.value = null
    drawerHistory.value = []
    isInternalNavigation.value = false
  }

  const updateDrawerTitle = (newTitle: string) => {
    // Update the current title
    drawerTitle.value = newTitle

    // Also update the title in the current drawer history item
    if (drawerHistory.value.length > 0) {
      const currentDrawer = drawerHistory.value[drawerHistory.value.length - 1]
      currentDrawer.title = newTitle
    }
  }

  // Compute if we can go back
  const canGoBack = computed(() => drawerHistory.value.length > 1)

  return {
    isDrawerOpen,
    drawerTitle,
    drawerContent,
    drawerHistory,
    canGoBack,
    isInternalNavigation,
    openDrawer,
    closeDrawer,
    goBackInDrawer,
    resetDrawerHistory,
    // Export this so components can set it before navigation
    setInternalNavigation: (value: boolean) => {
      isInternalNavigation.value = value
    },
    updateDrawerTitle,
    resetDrawerState, // Export this for testing
  }
}
